import type { DayZReport, ShiftCloseReport } from '@/types';
import { openThermalPrintWindow } from '@/utils/silentPrint';
import { formatMoney } from '@/utils/currency';
import i18n from '@/i18n';

function esc(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function money(n: number): string {
  return `${formatMoney(n || 0)}`;
}

function t(key: string, opts?: Record<string, unknown>): string {
  return String(i18n.t(key, opts));
}

function receiptLocale(): string {
  const lang = i18n.language || 'en';
  if (lang === 'ar') return 'ar-SA';
  if (lang === 'ur') return 'ur-PK';
  if (lang.startsWith('de')) return 'de-DE';
  if (lang.startsWith('fr')) return 'fr-FR';
  return 'en-GB';
}

function formatDateTime(value?: string): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const locale = receiptLocale();
  return (
    d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString(locale, { hour12: false, hour: '2-digit', minute: '2-digit' })
  );
}

function translateInvoiceStatus(status?: string): string {
  const s = String(status || '').trim();
  if (s === 'Paid') return t('shiftReceipt.paid');
  if (s === 'Partially Paid') return t('shiftReceipt.partiallyPaid');
  if (s === 'Unpaid') return t('shiftReceipt.unpaid');
  return s;
}

function reportTitle(report: ShiftCloseReport): string {
  if (report.report_type === 'X') return t('shiftReceipt.xReport');
  if (report.z_number) return t('shiftReceipt.zReportNumber', { number: report.z_number });
  return t('shiftReceipt.zReport');
}

export function browserPrintShiftReceipt(report: ShiftCloseReport): void {
  const store = report.company_name || 'Sale Desk';
  const title = reportTitle(report);
  const nonCash = Object.entries(report.non_cash_breakdown || {})
    .filter(([, amount]) => (amount || 0) > 0)
    .map(
      ([method, amount]) =>
        `<div class="row"><span>${esc(method)}</span><span>${money(amount)}</span></div>`,
    )
    .join('');

  const returnRows = (report.returns || [])
    .map(
      (r) =>
        `<tr>
          <td>${esc(r.name)}</td>
          <td class="num">${esc(r.pos_invoice)}</td>
          <td class="num">${money(r.refund_amount)}</td>
        </tr>`,
    )
    .join('');

  let detailSection = '';
  if (report.detail_type === 'item') {
    const itemRows = (report.items || [])
      .map(
        (row) =>
          `<tr>
            <td>${esc(row.item_name)}</td>
            <td class="num">${row.quantity}</td>
            <td class="num">${money(row.amount)}</td>
          </tr>`,
      )
      .join('');
    detailSection = `
      <div class="line"></div>
      <div class="center bold">${esc(t('shiftReceipt.itemsSold'))}</div>
      <table class="items">
        <colgroup>
          <col class="col-item" />
          <col class="col-qty" />
          <col class="col-amt" />
        </colgroup>
        <thead>
          <tr><th>${esc(t('shiftReceipt.itemCol'))}</th><th class="num">${esc(t('shiftReceipt.qtyCol'))}</th><th class="num">${esc(t('shiftReceipt.amountCol'))}</th></tr>
        </thead>
        <tbody>${itemRows || `<tr><td colspan="3">${esc(t('shiftReceipt.noItems'))}</td></tr>`}</tbody>
      </table>
    `;
  } else {
    const invoiceRows = (report.invoices || [])
      .map(
        (inv) =>
          `<tr>
            <td>${esc(inv.name)}</td>
            <td class="num">${money(inv.net_total)}</td>
            <td class="status">${esc(translateInvoiceStatus(inv.status))}</td>
          </tr>`,
      )
      .join('');
    detailSection = `
      <div class="line"></div>
      <div class="center bold">${esc(t('shiftReceipt.invoices'))}</div>
      <table class="items">
        <colgroup>
          <col class="col-invoice" />
          <col class="col-amount" />
          <col class="col-status" />
        </colgroup>
        <thead>
          <tr><th>${esc(t('shiftReceipt.invoiceCol'))}</th><th class="num">${esc(t('shiftReceipt.amountCol'))}</th><th class="status">${esc(t('shiftReceipt.statusCol'))}</th></tr>
        </thead>
        <tbody>${invoiceRows || `<tr><td colspan="3">${esc(t('shiftReceipt.noInvoices'))}</td></tr>`}</tbody>
      </table>
    `;
  }

  const isX = report.report_type === 'X';

  const html = `
    <div class="ticket">
      <div class="center bold big">${esc(store)}</div>
      <div class="center bold">${esc(title)}</div>
      ${
        isX
          ? `<div class="center small">${esc(report.reading_note || t('shiftReceipt.readingNote'))}</div>`
          : report.z_number
            ? `<div class="center small">${esc(t('shiftReceipt.zNumber'))}${report.z_number}</div>`
            : ''
      }
      <div class="line"></div>
      <div class="row"><span>${esc(t('shiftReceipt.cashier'))}</span><span>${esc(report.cashier_name || report.cashier || '-')}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.drawer'))}</span><span>${esc(report.pocket_name || '-')}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.shift'))}</span><span>${esc(report.name)}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.start'))}</span><span>${esc(formatDateTime(report.shift_start))}</span></div>
      <div class="row"><span>${esc(isX ? t('shiftReceipt.asOf') : t('shiftReceipt.end'))}</span><span>${esc(
        formatDateTime(isX ? report.generated_at : report.shift_end),
      )}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.openingCash'))}</span><span>${money(report.opening_amount)}</span></div>
      ${
        !isX
          ? `<div class="row"><span>${esc(t('shiftReceipt.closingCash'))}</span><span>${money(report.closing_amount || 0)}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.handover'))}</span><span>${money(report.cash_handover || 0)}</span></div>
      ${
        report.handover_mode === 'Bank' && report.handover_bank
          ? `<div class="row"><span>${esc(t('shiftReceipt.handoverToBank', { defaultValue: 'To Bank' }))}</span><span>${esc(report.handover_bank)}</span></div>`
          : ''
      }
      ${
        report.handover_mode === 'Cash' && report.handover_to_name
          ? `<div class="row"><span>${esc(t('shiftReceipt.handoverToManager', { defaultValue: 'To Manager' }))}</span><span>${esc(report.handover_to_name)}</span></div>`
          : ''
      }`
          : `<div class="row"><span>${esc(t('shiftReceipt.expectedCash'))}</span><span>${money(report.expected_cash || 0)}</span></div>`
      }
      <div class="line"></div>
      <div class="row"><span>${esc(t('shiftReceipt.invoices'))}</span><span>${report.invoice_count || 0}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.totalSales'))}</span><span>${money(report.total_sales || 0)}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.cashSales'))}</span><span>${money(report.cash_sales || 0)}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.cashReturns'))}</span><span>${money(report.cash_returns || report.total_returns || 0)}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.drawerExpenses'))}</span><span>${money(report.total_drawer_expenses || 0)}</span></div>
      ${(report.paid_in || 0) > 0 ? `<div class="row"><span>Paid In</span><span>${money(report.paid_in || 0)}</span></div>` : ''}
      ${(report.paid_out || 0) > 0 ? `<div class="row"><span>Paid Out</span><span>${money(report.paid_out || 0)}</span></div>` : ''}
      ${(report.drops || 0) > 0 ? `<div class="row"><span>Drops</span><span>${money(report.drops || 0)}</span></div>` : ''}
      ${nonCash}
      <div class="line"></div>
      <div class="center bold">${esc(t('shiftReceipt.returns'))}</div>
      <div class="row"><span>${esc(t('shiftReceipt.count'))}</span><span>${report.return_count || 0}</span></div>
      <div class="row bold"><span>${esc(t('shiftReceipt.totalRefund'))}</span><span>${money(report.total_returns || report.cash_returns || 0)}</span></div>
      ${
        returnRows
          ? `<table class="items">
        <colgroup>
          <col class="col-return" />
          <col class="col-ret-inv" />
          <col class="col-ret-amt" />
        </colgroup>
        <thead>
          <tr><th>${esc(t('shiftReceipt.returnCol'))}</th><th class="num">${esc(t('shiftReceipt.invoiceCol'))}</th><th class="num">${esc(t('shiftReceipt.amountCol'))}</th></tr>
        </thead>
        <tbody>${returnRows}</tbody>
      </table>`
          : `<div class="center small">${esc(t('shiftReceipt.noReturns'))}</div>`
      }
      ${detailSection}
      <div class="line"></div>
      <div class="center">${esc(isX ? t('shiftReceipt.endXReading') : t('shiftReceipt.endZReport'))}</div>
    </div>
  `;

  openThermalPrintWindow(html, `${isX ? 'X' : 'Z'}-${report.name}`);
}

export function browserPrintDayZReport(report: DayZReport): void {
  const store = report.company_name || 'Sale Desk';
  const tenders = Object.entries(report.tender_totals || {})
    .filter(([, amount]) => (amount || 0) > 0)
    .map(
      ([method, amount]) =>
        `<div class="row"><span>${esc(method)}</span><span>${money(amount)}</span></div>`,
    )
    .join('');

  const shiftRows = (report.shifts || [])
    .map(
      (s) =>
        `<tr>
          <td>${s.z_number != null ? s.z_number : '—'}</td>
          <td>${esc(s.cashier_name || s.cashier || '-')}</td>
          <td>${esc(s.pocket_name || '-')}</td>
          <td class="num">${money(s.total_sales || 0)}</td>
          <td class="num">${money(s.difference || 0)}</td>
        </tr>`,
    )
    .join('');

  const html = `
    <div class="ticket">
      <div class="center bold big">${esc(store)}</div>
      <div class="center bold">${esc(t('shiftReceipt.zDayReport'))}</div>
      <div class="center small">${esc(report.date)}</div>
      <div class="line"></div>
      <div class="row"><span>${esc(t('shiftReceipt.shifts'))}</span><span>${report.shift_count}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.invoices'))}</span><span>${report.invoice_count}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.returns'))}</span><span>${report.return_count}</span></div>
      <div class="row bold"><span>${esc(t('shiftReceipt.totalSales'))}</span><span>${money(report.total_sales)}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.cashSales'))}</span><span>${money(report.cash_sales)}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.cashReturns'))}</span><span>${money(report.cash_returns)}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.expenses'))}</span><span>${money(report.total_drawer_expenses)}</span></div>
      ${(report.paid_in || 0) > 0 ? `<div class="row"><span>Paid In</span><span>${money(report.paid_in || 0)}</span></div>` : ''}
      ${(report.paid_out || 0) > 0 ? `<div class="row"><span>Paid Out</span><span>${money(report.paid_out || 0)}</span></div>` : ''}
      ${(report.drops || 0) > 0 ? `<div class="row"><span>Drops</span><span>${money(report.drops || 0)}</span></div>` : ''}
      ${tenders}
      <div class="line"></div>
      <div class="row"><span>${esc(t('shiftReceipt.openingSum'))}</span><span>${money(report.opening_amount)}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.countedSum'))}</span><span>${money(report.closing_amount)}</span></div>
      <div class="row"><span>${esc(t('shiftReceipt.expectedSum'))}</span><span>${money(report.expected_cash)}</span></div>
      <div class="row bold"><span>${esc(t('shiftReceipt.varianceSum'))}</span><span>${money(report.difference)}</span></div>
      <div class="line"></div>
      <div class="center bold">${esc(t('shiftReceipt.shifts'))}</div>
      <table class="items">
        <thead>
          <tr><th>${esc(t('shiftReceipt.zNumber'))}</th><th>${esc(t('shiftReceipt.cashier'))}</th><th>${esc(t('shiftReceipt.drawer'))}</th><th class="num">${esc(t('shiftReceipt.totalSales'))}</th><th class="num">${esc(t('shiftReceipt.variance'))}</th></tr>
        </thead>
        <tbody>${shiftRows || `<tr><td colspan="5">${esc(t('shiftReceipt.noItems'))}</td></tr>`}</tbody>
      </table>
    </div>
  `;

  openThermalPrintWindow(html, `ZDAY-${report.date}`);
}
